import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiUpload, FiCheck } from "react-icons/fi";

const SiteSettings = () => {
  const [settings, setSettings] = useState({
    logoLight: "", logoDark: "", favicon: "",
    email: "larsonk503@gmail.com", phone: "12183041383", address: "",
    social: { facebook: "", instagram: "", twitter: "", youtube: "", whatsapp: "", reddit: "" },
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "site"));
        if (snap.exists()) {
          const data = snap.data();
          setSettings((prev) => ({
            ...prev, ...data,
            social: { ...prev.social, ...(data.social || {}) },
          }));
        }
      } catch (e) {}
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const compressImage = (file, maxW = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxW) { h = (maxW / w) * h; w = maxW; }
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.6));
        };
        img.onerror = () => reject(new Error("Failed to decode image"));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const base64 = await compressImage(file);
      setSettings((s) => ({ ...s, [field]: base64 }));
      await setDoc(doc(db, "settings", "site"), { [field]: base64 }, { merge: true });
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "site"), settings, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const UploadField = ({ label, field, preview }) => (
    <div className="bg-gray-50 rounded-xl p-5">
      <label className="block text-[12px] font-bold text-gray-600 mb-3">{label}</label>
      {settings[field] && (
        <img src={settings[field]} alt={label} className="h-16 w-auto object-contain mb-3 bg-white rounded border p-1" />
      )}
      <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
        <FiUpload size={14} />
        {uploading[field] ? "Uploading..." : `Upload ${label}`}
        <input type="file" accept="image/*" onChange={(e) => handleUpload(e, field)} className="hidden" disabled={uploading[field]} />
      </label>
    </div>
  );

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-black">Site Settings</h1>
          <p className="text-sm text-gray-400">Manage logos, social links, and site information</p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Logo Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Logo & Favicon</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <UploadField label="Logo (Light Mode)" field="logoLight" />
              <UploadField label="Logo (Dark Mode)" field="logoDark" />
              <UploadField label="Favicon" field="favicon" />
            </div>
            <p className="text-[11px] text-gray-400 mt-3">
              Upload your logo files. Light logo appears in header, dark logo in footer. Favicon shown in browser tab.
            </p>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Email</label>
                <input value={settings.email} onChange={(e) => setSettings((s) => ({ ...s, email: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="contact@example.com" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Phone</label>
                <input value={settings.phone} onChange={(e) => setSettings((s) => ({ ...s, phone: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="+1 (000) 000-0000" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Address</label>
                <textarea value={settings.address} onChange={(e) => setSettings((s) => ({ ...s, address: e.target.value }))} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="Street, City, State, ZIP" />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Social Media Links</h2>
            <p className="text-[12px] text-gray-400 mb-4">These links appear in the header and footer across the site.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: "Facebook", key: "facebook", placeholder: "https://facebook.com/..." },
                { label: "Instagram", key: "instagram", placeholder: "https://instagram.com/..." },
                { label: "Twitter / X", key: "twitter", placeholder: "https://twitter.com/..." },
                { label: "YouTube", key: "youtube", placeholder: "https://youtube.com/..." },
                { label: "WhatsApp", key: "whatsapp", placeholder: "https://wa.me/..." },
                { label: "Reddit", key: "reddit", placeholder: "https://reddit.com/r/..." },
              ].map((s) => (
                <div key={s.key}>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">{s.label}</label>
                  <input
                    type="url"
                    value={settings.social?.[s.key] || ""}
                    onChange={(e) => setSettings((prev) => ({ ...prev, social: { ...prev.social, [s.key]: e.target.value } }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]"
                    placeholder={s.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer Content */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Footer Content</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={settings.footerHeading || ""} onChange={(e) => setSettings((s) => ({ ...s, footerHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="About Simple Kindness" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Description</label>
                <textarea value={settings.footerDescription || ""} onChange={(e) => setSettings((s) => ({ ...s, footerDescription: e.target.value }))} rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" placeholder="About text for footer..." />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-[#e84fa5] text-white font-bold text-[13px] uppercase rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
              {saving ? "Saving..." : "Save Settings"}
            </button>
            {saved && (
              <span className="flex items-center gap-2 text-green-600 text-[13px] font-semibold">
                <FiCheck /> Settings saved!
              </span>
            )}
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default SiteSettings;
